import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getToken, API_PATH, request } from '../config/api';

const AuthContext = createContext(null);

function computeCanCreateEvents(role, expiresAt) {
    if (role === 'superadmin') return true;
    if (role !== 'admin') return false;
    if (!expiresAt) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(expiresAt) >= today;
}

const parseUserFromToken = (token) => {
    if (!token) return null;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const role = payload.role || 'pending';
        const expiresAt = payload.expiresAt || null;
        return {
            id: payload.id,
            name: payload.name || 'User',
            email: payload.email || '',
            mobile: payload.mobile || null,
            role,
            expiresAt,
            canCreateEvents: computeCanCreateEvents(role, expiresAt),
            coins: 0,
            subscriptionEndDate: null,
            canCreatePoll: role === 'superadmin',
            emailVerified: payload.emailVerified === true,
            // Not carried in the JWT itself — this is just the fast initial guess before
            // refreshUser()'s /me call corrects it moments later (see below). Defaulting true
            // is the safe assumption (most accounts have a real password); an SSO account
            // briefly shows "Change Password" instead of "Set Password" for that first instant.
            hasPassword: true,
        };
    } catch {
        return null;
    }
};

function getInitialUser() {
    let token = getToken();
    if (!token && typeof window !== 'undefined' && window.location.hash) {
        const match = window.location.hash.match(/[#&]token=([^&]*)/);
        if (match && match[1]) {
            try {
                token = decodeURIComponent(match[1]);
                sessionStorage.setItem('token', token);
                // SSO signup success: show congratulations once
                if (typeof window !== 'undefined' && window.location.search && /[?&]welcome=1/.test(window.location.search)) {
                    sessionStorage.setItem('showSsoWelcome', '1');
                }
                window.history.replaceState(null, '', window.location.pathname);
            } catch (e) {
                token = null;
            }
        }
    }
    return parseUserFromToken(token || getToken());
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(getInitialUser);

    const login = useCallback((token, rememberMe = false, userFromApi = null) => {
        if (rememberMe) localStorage.setItem('token', token);
        else sessionStorage.setItem('token', token);
        setUser(userFromApi || parseUserFromToken(token));
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        setUser(null);
    }, []);

    const refreshUser = useCallback(async () => {
        const token = getToken();
        if (!token) return;
        try {
            const res = await request(`${API_PATH}/auth/me`);
            if (res.ok) {
                const data = await res.json();
                setUser(data.user);
            }
        } catch {
            // ignore
        }
    }, []);

    useEffect(() => {
        const token = getToken();
        setUser(parseUserFromToken(token));
        if (token) refreshUser();
    }, [refreshUser]);

    const value = {
        user,
        isAuthenticated: !!user,
        login,
        logout,
        getToken,
        refreshUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
